import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import {
  UsersIcon, SparklesIcon, CubeIcon, ClipboardDocumentListIcon,
  ShoppingBagIcon, ChartBarIcon, ShieldCheckIcon, UserGroupIcon,
  ArrowRightIcon, ClockIcon, CalendarIcon, TagIcon, DocumentTextIcon,
} from '@heroicons/react/24/outline';

const SuperAdminDashboard = () => {
  const { userData, currentUser } = useAuth();
  // Face scan state removed
  // const [showFaceScan, setShowFaceScan] = useState(false);

  const userName  = userData?.personalInfo?.name || currentUser?.displayName || 'Super Admin';
  const firstName = userName.split(' ')[0];

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good Morning';
    if (h < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const superAdminCards = [
    { title: 'User Management',      description: 'Manage all users, roles and permissions',       path: '/superadmin/users',    icon: <UserGroupIcon  className="h-6 w-6" />, bgColor: 'bg-indigo-50', iconColor: 'text-indigo-600' },
    { title: 'Package Configuration', description: 'Configure membership packages and pricing',    path: '/superadmin/packages', icon: <ShieldCheckIcon className="h-6 w-6" />, bgColor: 'bg-rose-50',   iconColor: 'text-rose-600'   },
  ];

  // Updated admin cards - REMOVED Guest Management
  const adminCards = [
    // Guest Management card REMOVED
    { title: 'Activity Management', description: 'Create and manage gym activities',        path: '/admin/activities', icon: <SparklesIcon              className="h-6 w-6" />, bgColor: 'bg-purple-50',  iconColor: 'text-purple-600'  },
    { title: 'Products',            description: 'Add, edit and manage shop products',      path: '/admin/products',   icon: <CubeIcon                  className="h-6 w-6" />, bgColor: 'bg-blue-50',    iconColor: 'text-blue-600'    },
    { title: 'Inventory',           description: 'Track stock levels and manage inventory', path: '/admin/inventory',  icon: <ClipboardDocumentListIcon className="h-6 w-6" />, bgColor: 'bg-orange-50',  iconColor: 'text-orange-600'  },
    { title: 'Orders',              description: 'Process and manage customer orders',      path: '/admin/orders',     icon: <ShoppingBagIcon           className="h-6 w-6" />, bgColor: 'bg-cyan-50',    iconColor: 'text-cyan-600'    },
    { title: 'Sale',                description: 'Create and manage sales and discounts',   path: '/admin/sale',       icon: <TagIcon                   className="h-6 w-6" />, bgColor: 'bg-red-50',     iconColor: 'text-red-600'     },
    { title: 'Blog Management',     description: 'Create, edit and manage blog posts',      path: '/admin/blog',       icon: <DocumentTextIcon          className="h-6 w-6" />, bgColor: 'bg-teal-50',    iconColor: 'text-teal-600'    },
    { title: 'Reports',             description: 'View analytics and generate reports',     path: '/admin/reports',    icon: <ChartBarIcon              className="h-6 w-6" />, bgColor: 'bg-amber-50',   iconColor: 'text-amber-600'   },
  ];

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.05 } } };
  const itemVariants       = { hidden: { opacity: 0, y: 15 }, visible: { opacity: 1, y: 0 } };
  const sectionVariants    = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { delay: 0.2 } } };

  const DashCard = ({ card }) => (
    <motion.div variants={itemVariants} whileHover={{ y: -3 }} transition={{ type: 'spring', stiffness: 400, damping: 25 }}>
      <Link to={card.path}>
        <div className="group relative bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 hover:border-primary overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-0.5 bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 flex-1">
                <div className={`p-2 rounded-lg ${card.bgColor} group-hover:scale-105 transition-transform duration-300`}>
                  <div className={card.iconColor}>{card.icon}</div>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 group-hover:text-primary transition-colors duration-300">{card.title}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{card.description}</p>
                </div>
              </div>
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:shadow-md transition-all duration-300">
                <ArrowRightIcon className="h-3.5 w-3.5 text-primary group-hover:text-white transition-all duration-300" />
              </div>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* ── Welcome ── */}
        <motion.div initial={{ opacity: 0, y: -15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-3xl">👑</span>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  {getGreeting()}, <span className="text-primary">{firstName}</span>
                </h1>
              </div>
              <div className="flex items-center space-x-2">
                <p className="text-gray-500 text-sm">Super Admin Dashboard — Full system control</p>
                <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium text-white bg-primary">Super Admin</span>
              </div>
            </div>
            <div className="flex flex-col items-end">
              <div className="flex items-center space-x-2 text-gray-600">
                <ClockIcon className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                </span>
              </div>
              <div className="flex items-center space-x-2 text-gray-400 text-xs mt-0.5">
                <CalendarIcon className="h-3 w-3" />
                <span>{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ── FACE SCAN QUICK ACTION - COMPLETELY REMOVED ── */}
        {/* Face scan banner section removed */}

        {/* ── Super Admin Controls ── */}
        <motion.div variants={sectionVariants} initial="hidden" animate="visible" className="mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <ShieldCheckIcon className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Super Admin Controls</h2>
            <div className="flex-1 h-px bg-gray-200 ml-3" />
          </div>
          <motion.div variants={containerVariants} initial="hidden" animate="visible"
            className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {superAdminCards.map(card => <DashCard key={card.title} card={card} />)}
          </motion.div>
        </motion.div>

        {/* ── Admin Management ── */}
        <motion.div variants={sectionVariants} initial="hidden" animate="visible" transition={{ delay: 0.3 }}>
          <div className="flex items-center space-x-2 mb-4">
            <UsersIcon className="h-5 w-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Admin Management</h2>
            <div className="flex-1 h-px bg-gray-200 ml-3" />
          </div>
          <motion.div variants={containerVariants} initial="hidden" animate="visible"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {adminCards.map(card => <DashCard key={card.title} card={card} />)}
          </motion.div>
        </motion.div>
      </div>

      {/* ── Face Scan Modal - COMPLETELY REMOVED ── */}
    </div>
  );
};

export default SuperAdminDashboard;